import React, { useEffect, useState } from 'react';
import { Terminal } from 'lucide-react';
interface LoadingAnimationProps {
  status?: 'pending' | 'processing' | 'completed' | 'failed';
  selectedMode?: string;
}
const FORENSIC_LOGS = {
  pending: ['[INFO] Initializing Forensic Analysis Unit...', '[INFO] Queueing request...', '[INFO] Establishing secure connection...', '[INFO] Loading AI models...'],
  processing: ['[INFO] Parsing conversation metadata...', '[INFO] AI Deep Analysis in progress...', '[INFO] Identifying hidden manipulation markers...', '[INFO] Cross-referencing behavioral patterns...', '[INFO] Calculating toxicity vectors...']
};
export function LoadingAnimation({
  status = 'pending',
  selectedMode = 'default'
}: LoadingAnimationProps) {
  const [logs, setLogs] = useState<string[]>([]);
  const [currentLogIndex, setCurrentLogIndex] = useState(0);
  useEffect(() => {
    // Reset logs when status changes
    setLogs([]);
    setCurrentLogIndex(0);
  }, [status]);
  useEffect(() => {
    const logSet = status === 'processing' ? FORENSIC_LOGS.processing : FORENSIC_LOGS.pending;
    // Add mode-specific log for processing
    const modeLog = status === 'processing' ? `[INFO] Analyzing linguistic patterns for ${selectedMode}...` : null;
    const interval = setInterval(() => {
      setLogs(prev => {
        if (prev.length >= logSet.length + (modeLog ? 1 : 0)) {
          return prev;
        }
        const nextLog = prev.length === 2 && modeLog ? modeLog : logSet[prev.length - (modeLog && prev.length > 2 ? 1 : 0)];
        return [...prev, nextLog || logSet[prev.length]];
      });
      setCurrentLogIndex(prev => prev + 1);
    }, 800);
    return () => clearInterval(interval);
  }, [status, selectedMode]);
  return <div className="flex flex-col items-center justify-center w-full max-w-2xl mx-auto p-8" data-id="element-92">
      {/* Forensic Terminal Header */}
      <div className="w-full bg-[#0a0a0a] border border-[#00FFFF]/30 rounded-xl shadow-[0_0_15px_rgba(0,255,255,0.15)] mb-8 overflow-hidden" data-id="element-93">
        <div className="bg-[#00FFFF]/10 border-b border-[#00FFFF]/30 px-4 py-3 flex items-center gap-3" data-id="element-94">
          <Terminal size={16} className="text-[#00FFFF]" data-id="element-95" />
          <span className="text-[#00FFFF] font-mono text-xs uppercase tracking-widest font-bold" data-id="element-96">
            Forensic Analysis Terminal
          </span>
          <div className="ml-auto flex gap-1.5" data-id="element-97">
            <div className="w-2 h-2 rounded-full bg-[#00FFFF] animate-pulse" data-id="element-98"></div>
            <div className="w-2 h-2 rounded-full bg-[#00FFFF] animate-pulse delay-75" data-id="element-99"></div>
            <div className="w-2 h-2 rounded-full bg-[#00FFFF] animate-pulse delay-150" data-id="element-100"></div>
          </div>
        </div>

        {/* Terminal Content */}
        <div className="p-6 min-h-[300px] max-h-[400px] overflow-y-auto font-mono text-sm" data-id="element-101">
          {logs.map((log, index) => <div key={index} className="mb-3 animate-fade-in-up text-gray-300 leading-relaxed tracking-wide" style={{
          animationDelay: `${index * 100}ms`
        }} data-id="element-102">
              <span className="text-[#00FFFF] mr-2" data-id="element-103">{'>'}</span> {log}
            </div>)}

          {/* Blinking Cursor */}
          {logs.length > 0 && <div className="inline-flex items-center gap-2 mt-2" data-id="element-104">
              <span className="text-[#00FFFF]" data-id="element-105">{'>'}</span>
              <div className="w-2 h-4 bg-[#00FFFF] animate-pulse" data-id="element-106"></div>
            </div>}
        </div>
      </div>

      {/* Status Indicator */}
      <div className="text-center" data-id="element-107">
        {status === 'pending' && <p className="text-gray-400 text-sm font-mono animate-pulse flex items-center justify-center gap-2.5" data-id="element-108">
            <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse" data-id="element-109"></div>
            INITIALIZING FORENSIC ENGINE...
          </p>}
        {status === 'processing' && <p className="text-[#FF0000] text-sm font-mono animate-pulse flex items-center justify-center gap-2.5" data-id="element-110">
            <div className="w-2 h-2 bg-[#FF0000] rounded-full animate-pulse" data-id="element-111"></div>
            ⚠ ANALYZING TOXICITY PATTERNS...
          </p>}
        {status === 'completed' && <p className="text-green-500 text-sm font-mono flex items-center justify-center gap-2.5" data-id="element-112">
            <div className="w-2 h-2 bg-green-500 rounded-full" data-id="element-113"></div>✓ ANALYSIS
            COMPLETE. GENERATING REPORT...
          </p>}
      </div>
    </div>;
}